import proj4 from "proj4"
// import proj4List from "proj4-list" // proj4-list is heavy; only import when needed

export interface CoordinateReferenceSystem {
  code: string
  name: string
  description: string
  category: "Geographic" | "Projected"
  wkt?: string
  region?: string
  units: "degrees" | "meters"
  extent?: {
    minLon: number
    minLat: number
    maxLon: number
    maxLat: number
  }
}

export const COORDINATE_REFERENCE_SYSTEMS: CoordinateReferenceSystem[] = [
  // Geographic Coordinate Systems
  {
    code: "EPSG:4326",
    name: "WGS 84",
    description: "World Geodetic System 1984 - Global standard for GPS",
    category: "Geographic",
    region: "Global",
    units: "degrees",
  },
  {
    code: "EPSG:4283",
    name: "GDA94",
    description: "Geocentric Datum of Australia 1994",
    category: "Geographic",
    region: "Australia",
    units: "degrees",
    extent: {
      minLon: 108.0,
      minLat: -45.0,
      maxLon: 158.0,
      maxLat: -8.0,
    },
  },
  {
    code: "EPSG:7844",
    name: "GDA2020",
    description: "Geocentric Datum of Australia 2020",
    category: "Geographic",
    region: "Australia",
    units: "degrees",
    wkt: 'GEOGCS["GDA2020",DATUM["Geocentric_Datum_of_Australia_2020",SPHEROID["GRS 1980",6378137,298.257222101,AUTHORITY["EPSG","7019"]],AUTHORITY["EPSG","1168"]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","7844"]]',
    extent: {
      minLon: 108.0,
      minLat: -45.0,
      maxLon: 158.0,
      maxLat: -8.0,
    },
  },

  // Australian Projected Coordinate Systems (MGA - Map Grid of Australia)
  // Zone 49
  {
    code: "EPSG:28349",
    name: "GDA94 / MGA Zone 49",
    description: "Map Grid of Australia Zone 49 (Western Australia)",
    category: "Projected",
    region: "Western Australia",
    units: "meters",
    extent: {
      minLon: 108.0,
      minLat: -36.0,
      maxLon: 114.0,
      maxLat: -13.5,
    },
  },
  {
    code: "EPSG:7849",
    name: "GDA2020 / MGA Zone 49",
    description: "Map Grid of Australia Zone 49 (Western Australia) - GDA2020",
    category: "Projected",
    region: "Western Australia",
    units: "meters",
    wkt: 'PROJCS["GDA2020 / MGA zone 49",GEOGCS["GDA2020",DATUM["Geocentric_Datum_of_Australia_2020",SPHEROID["GRS 1980",6378137,298.257222101,AUTHORITY["EPSG","7019"]],AUTHORITY["EPSG","1168"]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","7844"]],PROJECTION["Transverse_Mercator"],PARAMETER["latitude_of_origin",0],PARAMETER["central_meridian",111],PARAMETER["scale_factor",0.9996],PARAMETER["false_easting",500000],PARAMETER["false_northing",10000000],UNIT["metre",1,AUTHORITY["EPSG","9001"]],AXIS["Easting",EAST],AXIS["Northing",NORTH],AUTHORITY["EPSG","7849"]]',
    extent: {
      minLon: 108.0,
      minLat: -36.0,
      maxLon: 114.0,
      maxLat: -13.5,
    },
  },

  // Zone 50
  {
    code: "EPSG:28350",
    name: "GDA94 / MGA Zone 50",
    description: "Map Grid of Australia Zone 50 (Western/Central Australia)",
    category: "Projected",
    region: "Western/Central Australia",
    units: "meters",
    extent: {
      minLon: 114.0,
      minLat: -36.0,
      maxLon: 120.0,
      maxLat: -13.5,
    },
  },
  {
    code: "EPSG:7850",
    name: "GDA2020 / MGA Zone 50",
    description:
      "Map Grid of Australia Zone 50 (Western/Central Australia) - GDA2020",
    category: "Projected",
    region: "Western/Central Australia",
    units: "meters",
    wkt: 'PROJCS["GDA2020 / MGA zone 50",GEOGCS["GDA2020",DATUM["Geocentric_Datum_of_Australia_2020",SPHEROID["GRS 1980",6378137,298.257222101,AUTHORITY["EPSG","7019"]],AUTHORITY["EPSG","1168"]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","7844"]],PROJECTION["Transverse_Mercator"],PARAMETER["latitude_of_origin",0],PARAMETER["central_meridian",117],PARAMETER["scale_factor",0.9996],PARAMETER["false_easting",500000],PARAMETER["false_northing",10000000],UNIT["metre",1,AUTHORITY["EPSG","9001"]],AXIS["Easting",EAST],AXIS["Northing",NORTH],AUTHORITY["EPSG","7850"]]',
    extent: {
      minLon: 114.0,
      minLat: -36.0,
      maxLon: 120.0,
      maxLat: -13.5,
    },
  },

  // Zone 51
  {
    code: "EPSG:28351",
    name: "GDA94 / MGA Zone 51",
    description: "Map Grid of Australia Zone 51 (Central Australia)",
    category: "Projected",
    region: "Central Australia",
    units: "meters",
    extent: {
      minLon: 120.0,
      minLat: -36.0,
      maxLon: 126.0,
      maxLat: -13.5,
    },
  },
  {
    code: "EPSG:7851",
    name: "GDA2020 / MGA Zone 51",
    description: "Map Grid of Australia Zone 51 (Central Australia) - GDA2020",
    category: "Projected",
    region: "Central Australia",
    units: "meters",
    wkt: 'PROJCS["GDA2020 / MGA zone 51",GEOGCS["GDA2020",DATUM["Geocentric_Datum_of_Australia_2020",SPHEROID["GRS 1980",6378137,298.257222101,AUTHORITY["EPSG","7019"]],AUTHORITY["EPSG","1168"]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","7844"]],PROJECTION["Transverse_Mercator"],PARAMETER["latitude_of_origin",0],PARAMETER["central_meridian",123],PARAMETER["scale_factor",0.9996],PARAMETER["false_easting",500000],PARAMETER["false_northing",10000000],UNIT["metre",1,AUTHORITY["EPSG","9001"]],AXIS["Easting",EAST],AXIS["Northing",NORTH],AUTHORITY["EPSG","7851"]]',
    extent: {
      minLon: 120.0,
      minLat: -36.0,
      maxLon: 126.0,
      maxLat: -13.5,
    },
  },

  // Zone 52
  {
    code: "EPSG:28352",
    name: "GDA94 / MGA Zone 52",
    description: "Map Grid of Australia Zone 52 (Central/Eastern Australia)",
    category: "Projected",
    region: "Central/Eastern Australia",
    units: "meters",
    extent: {
      minLon: 126.0,
      minLat: -36.0,
      maxLon: 132.0,
      maxLat: -13.5,
    },
  },
  {
    code: "EPSG:7852",
    name: "GDA2020 / MGA Zone 52",
    description:
      "Map Grid of Australia Zone 52 (Central/Eastern Australia) - GDA2020",
    category: "Projected",
    region: "Central/Eastern Australia",
    units: "meters",
    wkt: 'PROJCS["GDA2020 / MGA zone 52",GEOGCS["GDA2020",DATUM["Geocentric_Datum_of_Australia_2020",SPHEROID["GRS 1980",6378137,298.257222101,AUTHORITY["EPSG","7019"]],AUTHORITY["EPSG","1168"]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","7844"]],PROJECTION["Transverse_Mercator"],PARAMETER["latitude_of_origin",0],PARAMETER["central_meridian",129],PARAMETER["scale_factor",0.9996],PARAMETER["false_easting",500000],PARAMETER["false_northing",10000000],UNIT["metre",1,AUTHORITY["EPSG","9001"]],AXIS["Easting",EAST],AXIS["Northing",NORTH],AUTHORITY["EPSG","7852"]]',
    extent: {
      minLon: 126.0,
      minLat: -36.0,
      maxLon: 132.0,
      maxLat: -13.5,
    },
  },

  // Zone 53
  {
    code: "EPSG:28353",
    name: "GDA94 / MGA Zone 53",
    description: "Map Grid of Australia Zone 53 (Eastern Australia)",
    category: "Projected",
    region: "Eastern Australia",
    units: "meters",
    extent: {
      minLon: 132.0,
      minLat: -36.0,
      maxLon: 138.0,
      maxLat: -13.5,
    },
  },
  {
    code: "EPSG:7853",
    name: "GDA2020 / MGA Zone 53",
    description: "Map Grid of Australia Zone 53 (Eastern Australia) - GDA2020",
    category: "Projected",
    region: "Eastern Australia",
    units: "meters",
    wkt: 'PROJCS["GDA2020 / MGA zone 53",GEOGCS["GDA2020",DATUM["Geocentric_Datum_of_Australia_2020",SPHEROID["GRS 1980",6378137,298.257222101,AUTHORITY["EPSG","7019"]],AUTHORITY["EPSG","1168"]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","7844"]],PROJECTION["Transverse_Mercator"],PARAMETER["latitude_of_origin",0],PARAMETER["central_meridian",135],PARAMETER["scale_factor",0.9996],PARAMETER["false_easting",500000],PARAMETER["false_northing",10000000],UNIT["metre",1,AUTHORITY["EPSG","9001"]],AXIS["Easting",EAST],AXIS["Northing",NORTH],AUTHORITY["EPSG","7853"]]',
    extent: {
      minLon: 132.0,
      minLat: -36.0,
      maxLon: 138.0,
      maxLat: -13.5,
    },
  },

  // Zone 54
  {
    code: "EPSG:28354",
    name: "GDA94 / MGA Zone 54",
    description: "Map Grid of Australia Zone 54 (Eastern Australia)",
    category: "Projected",
    region: "Eastern Australia",
    units: "meters",
    extent: {
      minLon: 138.0,
      minLat: -36.0,
      maxLon: 144.0,
      maxLat: -13.5,
    },
  },
  {
    code: "EPSG:7854",
    name: "GDA2020 / MGA Zone 54",
    description: "Map Grid of Australia Zone 54 (Eastern Australia) - GDA2020",
    category: "Projected",
    region: "Eastern Australia",
    units: "meters",
    wkt: 'PROJCS["GDA2020 / MGA zone 54",GEOGCS["GDA2020",DATUM["Geocentric_Datum_of_Australia_2020",SPHEROID["GRS 1980",6378137,298.257222101,AUTHORITY["EPSG","7019"]],AUTHORITY["EPSG","1168"]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","7844"]],PROJECTION["Transverse_Mercator"],PARAMETER["latitude_of_origin",0],PARAMETER["central_meridian",141],PARAMETER["scale_factor",0.9996],PARAMETER["false_easting",500000],PARAMETER["false_northing",10000000],UNIT["metre",1,AUTHORITY["EPSG","9001"]],AXIS["Easting",EAST],AXIS["Northing",NORTH],AUTHORITY["EPSG","7854"]]',
    extent: {
      minLon: 138.0,
      minLat: -36.0,
      maxLon: 144.0,
      maxLat: -13.5,
    },
  },

  // Zone 55
  {
    code: "EPSG:28355",
    name: "GDA94 / MGA Zone 55",
    description: "Map Grid of Australia Zone 55 (Eastern Australia, NSW/VIC)",
    category: "Projected",
    region: "NSW/VIC/TAS",
    units: "meters",
    extent: {
      minLon: 144.0,
      minLat: -44.0,
      maxLon: 150.0,
      maxLat: -13.5,
    },
  },
  {
    code: "EPSG:7855",
    name: "GDA2020 / MGA Zone 55",
    description:
      "Map Grid of Australia Zone 55 (Eastern Australia, NSW/VIC) - GDA2020",
    category: "Projected",
    region: "NSW/VIC/TAS",
    units: "meters",
    wkt: 'PROJCS["GDA2020 / MGA zone 55",GEOGCS["GDA2020",DATUM["Geocentric_Datum_of_Australia_2020",SPHEROID["GRS 1980",6378137,298.257222101,AUTHORITY["EPSG","7019"]],AUTHORITY["EPSG","1168"]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","7844"]],PROJECTION["Transverse_Mercator"],PARAMETER["latitude_of_origin",0],PARAMETER["central_meridian",147],PARAMETER["scale_factor",0.9996],PARAMETER["false_easting",500000],PARAMETER["false_northing",10000000],UNIT["metre",1,AUTHORITY["EPSG","9001"]],AXIS["Easting",EAST],AXIS["Northing",NORTH],AUTHORITY["EPSG","7855"]]',
    extent: {
      minLon: 144.0,
      minLat: -44.0,
      maxLon: 150.0,
      maxLat: -13.5,
    },
  },

  // Zone 56
  {
    code: "EPSG:28356",
    name: "GDA94 / MGA Zone 56",
    description: "Map Grid of Australia Zone 56 (Eastern Australia, NSW/QLD)",
    category: "Projected",
    region: "NSW/QLD",
    units: "meters",
    extent: {
      minLon: 150.0,
      minLat: -36.0,
      maxLon: 156.0,
      maxLat: -13.5,
    },
  },
  {
    code: "EPSG:7856",
    name: "GDA2020 / MGA Zone 56",
    description:
      "Map Grid of Australia Zone 56 (Eastern Australia, NSW/QLD) - GDA2020",
    category: "Projected",
    region: "NSW/QLD",
    units: "meters",
    wkt: 'PROJCS["GDA2020 / MGA zone 56",GEOGCS["GDA2020",DATUM["Geocentric_Datum_of_Australia_2020",SPHEROID["GRS 1980",6378137,298.257222101,AUTHORITY["EPSG","7019"]],AUTHORITY["EPSG","1168"]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","7844"]],PROJECTION["Transverse_Mercator"],PARAMETER["latitude_of_origin",0],PARAMETER["central_meridian",153],PARAMETER["scale_factor",0.9996],PARAMETER["false_easting",500000],PARAMETER["false_northing",10000000],UNIT["metre",1,AUTHORITY["EPSG","9001"]],AXIS["Easting",EAST],AXIS["Northing",NORTH],AUTHORITY["EPSG","7856"]]',
    extent: {
      minLon: 150.0,
      minLat: -36.0,
      maxLon: 156.0,
      maxLat: -13.5,
    },
  },
]

/**
 * Get CRS by EPSG code
 */
export function getCRSByCode(
  code: string,
): CoordinateReferenceSystem | undefined {
  return COORDINATE_REFERENCE_SYSTEMS.find((crs) => crs.code === code)
}

/**
 * Get CRS options grouped by category
 */
export function getCRSGroupedByCategory() {
  const grouped = COORDINATE_REFERENCE_SYSTEMS.reduce(
    (acc, crs) => {
      if (!acc[crs.category]) {
        acc[crs.category] = []
      }
      acc[crs.category].push(crs)
      return acc
    },
    {} as Record<string, CoordinateReferenceSystem[]>,
  )

  return grouped
}

/**
 * Get suggested CRS based on location
 */
export function getSuggestedCRS(
  longitude?: number,
  latitude?: number,
): CoordinateReferenceSystem[] {
  if (!longitude || !latitude) {
    return [getCRSByCode("EPSG:4326")!] // Default to WGS84
  }

  // Check if in Australia
  if (
    longitude >= 108 &&
    longitude <= 158 &&
    latitude >= -45 &&
    latitude <= -8
  ) {
    const suggestions: CoordinateReferenceSystem[] = []

    // Add appropriate MGA zone
    const utmZones = COORDINATE_REFERENCE_SYSTEMS.filter(
      (crs) =>
        crs.category === "Projected" &&
        crs.extent &&
        longitude >= crs.extent.minLon &&
        longitude <= crs.extent.maxLon,
    )

    // Prefer GDA2020 over GDA94
    const gda2020Zone = utmZones.find((crs) => crs.code.includes("7"))
    const gda94Zone = utmZones.find((crs) => crs.code.includes("28"))

    if (gda2020Zone) suggestions.push(gda2020Zone)
    if (gda94Zone) suggestions.push(gda94Zone)

    // Add GDA2020 and GDA94 geographic
    suggestions.push(getCRSByCode("EPSG:7844")!) // GDA2020
    suggestions.push(getCRSByCode("EPSG:4283")!) // GDA94

    return suggestions
  }

  // Default to WGS84 for non-Australian locations
  return [getCRSByCode("EPSG:4326")!]
}

const minimalProj4List: Record<string, [string, string]> = {
  "EPSG:4326": ["EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs"],
  "EPSG:4283": [
    "EPSG:4283",
    "+proj=longlat +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +no_defs",
  ],
  "EPSG:28349": [
    "EPSG:28349",
    "+proj=utm +zone=49 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
  ],
  "EPSG:28350": [
    "EPSG:28350",
    "+proj=utm +zone=50 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
  ],
  "EPSG:28351": [
    "EPSG:28351",
    "+proj=utm +zone=51 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
  ],
  "EPSG:28352": [
    "EPSG:28352",
    "+proj=utm +zone=52 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
  ],
  "EPSG:28353": [
    "EPSG:28353",
    "+proj=utm +zone=53 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
  ],
  "EPSG:28354": [
    "EPSG:28354",
    "+proj=utm +zone=54 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
  ],
  "EPSG:28355": [
    "EPSG:28355",
    "+proj=utm +zone=55 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
  ],
  "EPSG:28356": [
    "EPSG:28356",
    "+proj=utm +zone=56 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
  ],
}

// initialise proj4 with all available projections
const getProjectionJson = async (epsgCode: string): Promise<string> => {
  const projJson = await fetch(
    `https://spatialreference.org/ref/epsg/${epsgCode.split(":")[1]}/ogcwkt/`,
  )
  return await projJson.text()
}

Promise.all(
  COORDINATE_REFERENCE_SYSTEMS.map(async ({ code, wkt }) => {
    if (minimalProj4List[code]) {
      return minimalProj4List[code]
    }
    if (wkt) return [code, wkt] as [string, string]
    const proj = await getProjectionJson(code)
    return [code, proj] as [string, string]
  }),
).then((projections) => {
  proj4.defs(projections)
})

export { proj4 }
